"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.sanitizeAndFindFormattingTags = exports.createSanitize = exports.convertGQLRTEConfigToRTEFeatures = exports.ALL_FEATURES = exports.WORDLIST_FORBID_TAGS = void 0;
const dompurify_1 = __importDefault(require("dompurify"));
const constants_1 = require("../constants");
/** Tags that we remove before looking for suspect/banned words */
exports.WORDLIST_FORBID_TAGS = [
    "a",
    "b",
    "strong",
    "i",
    "em",
    "s",
    "del",
    "ins",
    "mark",
    "cite",
    "q",
    "samp",
    "small",
    "sup",
    "sub",
    "span",
    "u",
    "code",
    "time",
    "var",
    "wbr",
    "kbd",
    "abbr",
];
/**
 * ALL_FEATURES is a predefined map of RTEFeatures with all
 * features turned on
 */
exports.ALL_FEATURES = {
    bold: true,
    italic: true,
    blockquote: true,
    bulletList: true,
    strikethrough: true,
    spoiler: true,
    sarcasm: true,
};
/**
 * convertGQLRTEConfigToRTEFeatures turns the
 * RTE configuration from the GraphQL Schema to
 * RTEFeatures.
 */
function convertGQLRTEConfigToRTEFeatures(config) {
    return {
        bold: config.enabled,
        italic: config.enabled,
        blockquote: config.enabled,
        bulletList: config.enabled,
        strikethrough: config.enabled && config.strikethrough,
        spoiler: config.enabled && config.spoiler,
        sarcasm: config.enabled && config.sarcasm,
    };
}
exports.convertGQLRTEConfigToRTEFeatures = convertGQLRTEConfigToRTEFeatures;
const MAILTO_PROTOCOL = "mailto:";
/**
 * Ensure that each anchor tag is replaced with text that
 * corresponds to its inner html. If the tag's href matches
 * its inner html, it remains as is.
 */
const sanitizeAnchor = (node) => {
    if (node.nodeName === "A") {
        let href = node.getAttribute("href");
        let textContent = node.textContent;
        let mailToWithMatchingInnerHtml = false, invalidURL = false;
        if (href) {
            let url;
            try {
                url = new URL(href);
            }
            catch (error) {
                invalidURL = true;
            }
            // Check for a mailto: link with corresponding inner html
            if (url && url.protocol === MAILTO_PROTOCOL) {
                if (href.replace(url.protocol, "") === textContent) {
                    mailToWithMatchingInnerHtml = true;
                }
            }
            // Account for whether trailing slashes are included or not
            href = (href === null || href === void 0 ? void 0 : href.endsWith("/")) ? href : (href += "/");
            textContent = (textContent === null || textContent === void 0 ? void 0 : textContent.endsWith("/")) ? textContent
                : (textContent += "/");
        }
        // When the url is valid and the anchor tag's inner html matches its href
        if (!invalidURL &&
            ((href && href === textContent) || mailToWithMatchingInnerHtml)) {
            // Ensure we wrap all the links with the target + rel set
            node.setAttribute("target", "_blank");
            node.setAttribute("rel", "noopener noreferrer ugc");
            node.classList.add("coral-comment-content-link");
        }
        else {
            // Otherwise, turn the anchor link into text corresponding to its inner html
            node.insertAdjacentText("beforebegin", node.innerHTML);
            node.parentNode.removeChild(node);
        }
    }
};
/**
 * Further restrict the use of attributes.
 */
const sanitizeAttributes = (features, node) => {
    // Only achor tags can have the `href` attribute.
    if (node.nodeName !== "A" && node.getAttribute("href")) {
        node.removeAttribute("href");
    }
    if (features.spoiler || features.sarcasm) {
        // Only allow <span class="SPOILER_CLASSNAME"> as our spoiler tag.
        if (node.nodeName === "SPAN") {
            if (features.spoiler && node.classList.contains(constants_1.SPOILER_CLASSNAME)) {
                // Remove other classes.
                node.className = constants_1.SPOILER_CLASSNAME;
            }
            else if (features.sarcasm &&
                node.classList.contains(constants_1.SARCASM_CLASSNAME)) {
                // Remove other classes.
                node.className = constants_1.SARCASM_CLASSNAME;
            }
            else {
                node.removeAttribute("class");
            }
        }
        else {
            node.removeAttribute("class");
        }
    }
};
function createPurifyConfig(features) {
    const ALLOWED_TAGS = ["a", "br", "div", "p"];
    const ALLOWED_ATTR = [
        // Allow href tags for anchor tags.
        "href",
    ];
    if (features.bold) {
        ALLOWED_TAGS.push("b", "strong");
    }
    if (features.italic) {
        ALLOWED_TAGS.push("i", "em");
    }
    if (features.blockquote) {
        ALLOWED_TAGS.push("blockquote");
    }
    if (features.bulletList) {
        ALLOWED_TAGS.push("ul", "li");
    }
    if (features.strikethrough) {
        ALLOWED_TAGS.push("s");
    }
    if (features.spoiler || features.sarcasm) {
        ALLOWED_TAGS.push("span");
        ALLOWED_ATTR.push("class");
    }
    return {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        ALLOW_DATA_ATTR: false,
        // `ALLOW_ARIA_ATTR` not typed as for v2.0.1.
        ALLOW_ARIA_ATTR: false,
    };
}
// Source for constant: https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType
// Using this instead of Node.TEXT_NODE because Node is not defined in Node.js
const TEXT_NODE_TYPE = 3;
function createSanitize(window, options) {
    // Initializing JSDOM and DOMPurify
    const purify = dompurify_1.default(window);
    const features = (options === null || options === void 0 ? void 0 : options.features) || {};
    // Setting our DOMPurify config.
    purify.setConfig({
        ...createPurifyConfig(features),
        // Always return the DOM to the caller of sanitize.
        RETURN_DOM: true,
        ...options === null || options === void 0 ? void 0 : options.config,
    });
    purify.addHook("afterSanitizeAttributes", sanitizeAttributes.bind(null, features));
    purify.addHook("afterSanitizeAttributes", sanitizeAnchor);
    purify.addHook("afterSanitizeElements", (n) => {
        // Replace nbsp, including those inserted when sanitizing
        // anchor tags and replacing them with their text
        if (n.nodeType === TEXT_NODE_TYPE && n.nodeValue) {
            n.nodeValue = n.nodeValue.replace(/\xA0/g, " ");
        }
    });
    if (options === null || options === void 0 ? void 0 : options.normalize) {
        purify.addHook("afterSanitizeElements", (n) => {
            var _a, _b;
            if (n.nodeType === TEXT_NODE_TYPE &&
                ((_a = n.previousSibling) === null || _a === void 0 ? void 0 : _a.nodeType) === TEXT_NODE_TYPE) {
                // Merge text node sublings together.
                // eslint-disable-next-line no-unused-expressions
                (_b = n.parentNode) === null || _b === void 0 ? void 0 : _b.normalize();
            }
        });
    }
    if (options === null || options === void 0 ? void 0 : options.modify) {
        options.modify(purify);
    }
    return purify.sanitize.bind(purify);
}
exports.createSanitize = createSanitize;
/**
 * Sanitize html content and find spoiler and sarcasm tags.
 */
exports.sanitizeAndFindFormattingTags = (() => {
    /** Resused instance */
    let sanitize = null;
    /** Found spoiler tags during sanitization will be placed here. */
    let spoilerTags = [];
    const sarcasmTags = [];
    const blockquoteTags = [];
    const linkTags = [];
    return (window, source) => {
        if (!sanitize) {
            sanitize = createSanitize(window, {
                // Allow all rte features to be displayed.
                features: exports.ALL_FEATURES,
                modify: (purify) => {
                    // Add a hook that detects spoiler tags and adds to `spoilerTags` array
                    purify.addHook("afterSanitizeAttributes", (node) => {
                        if (node.tagName === "SPAN" &&
                            node.className === constants_1.SPOILER_CLASSNAME) {
                            spoilerTags.push(node);
                        }
                        if (node.tagName === "SPAN" &&
                            node.className === constants_1.SARCASM_CLASSNAME) {
                            sarcasmTags.push(node);
                        }
                        if (node.tagName === "BLOCKQUOTE") {
                            blockquoteTags.push(node);
                        }
                        if (node.tagName === "A" && node.className === constants_1.LINK_CLASSNAME) {
                            linkTags.push(node);
                        }
                    });
                },
            });
        }
        const sanitized = sanitize(source);
        const ret = spoilerTags;
        const sarcasm = sarcasmTags;
        const blockquote = blockquoteTags;
        const links = linkTags;
        spoilerTags = [];
        return [sanitized, ret, sarcasm, blockquote, links];
    };
})();
